import { api } from "@/integrations/clients/carAuction/carAuctionClient";
import { compressImageForFullSize } from "@/utils/imageCompression";
import {
  SupportCaseDetails,
  CreateSupportCaseRequest,
} from "@/types/support";
import {
  ApiCase,
  ApiMessage,
  buildCaseDescription,
  buildCaseTitle,
  mapApiCaseToDetails,
  mapFrontendCategoryToApi,
  mapFrontendStatusToApi,
} from "./supportApiMappers";

interface ApiListResponse {
  success: boolean;
  data?: ApiCase[];
  count?: number;
  total?: number;
}

interface ApiItemResponse<T> {
  success: boolean;
  data?: T;
}

export class SupportCaseService {
  private static async prepareFileForUpload(file: File): Promise<File> {
    if (!file.type.startsWith("image/")) {
      return file;
    }
    const compressed = await compressImageForFullSize(file);
    if (compressed.name === file.name) {
      return compressed;
    }
    return new File([compressed], file.name, {
      type: compressed.type || file.type,
      lastModified: Date.now(),
    });
  }

  static async getMySupportCases(
    _userId: string,
    filters?: { status?: string; page?: number; limit?: number },
  ): Promise<{
    cases: SupportCaseDetails[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    const limit = filters?.limit || 10;
    const page = filters?.page || 1;
    const offset = (page - 1) * limit;

    const response = await api.get<ApiListResponse>("/support/cases", {
      params: {
        limit,
        offset,
        status: mapFrontendStatusToApi(
          filters?.status && filters.status !== "all"
            ? filters.status
            : undefined,
        ),
      },
    });

    if (!response.success || !response.data) {
      return { cases: [], total: 0, page, totalPages: 1 };
    }

    const total = response.total ?? response.data.length;
    return {
      cases: response.data.map(mapApiCaseToDetails),
      total,
      page,
      totalPages: Math.ceil(total / limit) || 1,
    };
  }

  static async getSupportCase(caseId: string): Promise<SupportCaseDetails> {
    const response = await api.get<ApiItemResponse<ApiCase>>(
      `/support/cases/${caseId}`,
    );
    if (!response.success || !response.data) {
      throw new Error("Support case not found");
    }
    return mapApiCaseToDetails(response.data);
  }

  static async getCaseMessages(caseId: string) {
    const response = await api.get<ApiListResponse>(
      `/support/cases/${caseId}/messages`,
    );
    if (!response.success || !response.data) return [];
    return response.data;
  }

  static async sendCaseMessage(
    caseId: string,
    content: string,
  ): Promise<ApiMessage> {
    const response = await api.post<ApiItemResponse<ApiMessage>>(
      `/support/cases/${caseId}/messages`,
      { content },
    );
    if (!response.success || !response.data) {
      throw new Error("Failed to send message");
    }
    return response.data;
  }

  static async createSupportCase(
    request: CreateSupportCaseRequest,
  ): Promise<SupportCaseDetails> {
    const { category, tags } = mapFrontendCategoryToApi(
      request.type,
      request.category,
    );

    const response = await api.post<ApiItemResponse<ApiCase>>(
      "/support/cases",
      {
        type: request.type,
        title: buildCaseTitle(request.type, request.category),
        description: buildCaseDescription(request.description, request.regNr),
        category,
        priority: "medium",
        tags,
        damageLocation: request.damageLocation,
        damageType: request.damageType,
        engineComponent: request.engineComponent,
        interiorSection: request.interiorSection,
        wheelType: request.wheelType,
      },
    );

    if (!response.success || !response.data) {
      throw new Error("Failed to create support case");
    }

    const created = mapApiCaseToDetails(response.data);

    if (request.files?.length) {
      for (const file of request.files) {
        const prepared = await this.prepareFileForUpload(file);
        const formData = new FormData();
        formData.append("file", prepared, prepared.name);
        formData.append("name", prepared.name);
        formData.append("skipHistory", "true");
        await api.post(`/support/cases/${created.id}/documents`, formData);
      }
      return this.getSupportCase(created.id);
    }

    return created;
  }

  static async lookupComplaintVehicle(registrationNo: string): Promise<boolean> {
    const normalized = registrationNo.replace(/\s/g, "").toUpperCase();
    const response = await api.get<{
      success: boolean;
      data?: { found: boolean };
    }>(`/support/vehicle-lookup/${encodeURIComponent(normalized)}`);
    return Boolean(response.success && response.data?.found);
  }

  static async uploadCaseDocument(caseId: string, file: File): Promise<void> {
    const prepared = await this.prepareFileForUpload(file);
    const formData = new FormData();
    formData.append("file", prepared, prepared.name);
    formData.append("name", prepared.name);
    await api.post(`/support/cases/${caseId}/documents`, formData);
  }

  static async deleteCaseDocument(
    caseId: string,
    documentId: string,
  ): Promise<void> {
    await api.delete(`/support/cases/${caseId}/documents/${documentId}`);
  }

  static async getUnreadSummary(isAdmin: boolean): Promise<{
    count: number;
    hasUnread: boolean;
  }> {
    const path = isAdmin
      ? "/admin/support/cases/unread-summary"
      : "/support/unread-summary";
    const response = await api.get<{
      success: boolean;
      data?: { count: number; hasUnread: boolean };
    }>(path);
    return response.data ?? { count: 0, hasUnread: false };
  }
}
